import Events from "../Utils/Events";
import * as Schema from "./ProductPageStateSchema";
import * as Util from "../Utils/Util";
import ProductState from "./ProductState";
import { Product } from "./ProductPageStateSchema";
import { ProductsForCategoryAPIResult, findByCodeAndQuantity, findByMedicineType } from "../Utils/PriceLoader";

export default class ProductPageState {
  private static INSTANCE?: ProductPageState;

  private readonly products: Schema.Products;
  private product: Schema.Product;
  private strength: Schema.Strength;
  private frequency?: Schema.SubscriptionFrequency;
  private quantity: Schema.Quantity;

  private constructor(pricesOverride: any | undefined) {
    this.products = Schema.load(Util.loadJSON("product-schema"), Util.loadJSON("defaults-schema"));

    this.product = this.findProduct();
    this.strength = this.findStrength();
    this.frequency = this.findFrequency();
    this.quantity = this.findQuantity();

    if (pricesOverride) {
      const newProductMap: Map<string, Product> = new Map();
      this.products.products.forEach((product: Product) => {
        const newProduct = this.mergePricesIntoProduct(product, pricesOverride);
        newProductMap.set(newProduct.code, newProduct);
      });
      this.products.products = newProductMap;
    }

    this.publishStateChange();

    Events.on("product-changed", (c: string) => this.handleProductChange(c));
    Events.on("strength-changed", (c: string) => this.handleStrengthChange(c));
    Events.on("subscription-toggled", (c: boolean) => this.handleSubscriptionToggle(c));
    Events.on("frequency-changed", (c: string) => this.handleFrequencyChange(c));
    Events.on("quantity-changed", (c: string) => this.handleQuantityChange(c));
    Events.on("product-state-change-request", (_c: string) => this.publishStateChange());
  }

  private findByProperty(object: Array<any>, property: string, value: string): any {
    return object.find((o: any) => o[property] == value);
  }

  private mergePricesIntoProduct(currentProduct: Product, prices: ProductsForCategoryAPIResult): Product {
    const pricesOverrideForProduct = findByMedicineType(prices, currentProduct.medicineType);
    const newProduct: Product = { ...currentProduct };
    for (const [strengthName, currentStrength] of newProduct.strengths) {
      for (const [quantityName, currentQuantity] of currentStrength.quantities) {
        const productPrice = findByCodeAndQuantity(
          pricesOverrideForProduct,
          currentProduct.code + strengthName,
          quantityName
        );
        if (productPrice) {
          currentQuantity.price = productPrice.price;
          if (currentQuantity.discount) {
            currentQuantity.discount = {
              ...currentQuantity.discount,
              priceAfterDiscount: productPrice.priceWithDiscount,
            };
          }
        }
      }
    }
    return newProduct;
  }

  private findProduct(): Schema.Product {
    return this.products.products.get(this.products.currentProduct)!;
  }

  private findStrength(): Schema.Strength {
    const strength = this.product.strengths.get(this.product.currentStrength);
    this.product.currentStrength = strength!.code;
    return strength!;
  }

  private findFrequency(): Schema.SubscriptionFrequency | undefined {
    const frequency: Schema.SubscriptionFrequency | undefined = this.strength.currentFrequency
      ? this.strength.subscriptionFrequencies.get(this.strength.currentFrequency)
      : undefined;
    this.strength.currentFrequency = frequency?.code;
    return frequency;
  }

  private findQuantity(): Schema.Quantity {
    let quantity: Schema.Quantity | null;

    if (this.frequency) {
      quantity = this.frequency.quantities.get(this.frequency.currentQuantity)!;
      this.frequency.currentQuantity = quantity.code;
      return quantity;
    } else {
      quantity = this.strength.quantities.get(this.strength.currentOTPQuantity)!;
      this.strength.currentOTPQuantity = quantity.code;
      return quantity;
    }
  }

  private handleProductChange(code: string): void {
    this.products.currentProduct = code;
    this.product = this.findProduct();
    this.strength = this.findStrength();
    this.frequency = this.findFrequency();
    this.quantity = this.findQuantity();
    this.publishStateChange();
  }

  private handleStrengthChange(code: string): void {
    this.product.currentStrength = code;
    this.strength = this.findStrength();
    this.frequency = this.findFrequency();
    this.quantity = this.findQuantity();
    this.publishStateChange();
  }

  private handleSubscriptionToggle(on: boolean) {
    const defaultFreq = Array.from(this.strength.subscriptionFrequencies.entries())[0];
    this.handleFrequencyChange(on ? defaultFreq[0] : undefined);
  }

  private handleFrequencyChange(code: string | undefined): void {
    this.strength.currentFrequency = code;
    this.frequency = this.findFrequency();
    this.quantity = this.findQuantity();
    this.publishStateChange();
  }

  private handleQuantityChange(code: string): void {
    if (this.frequency) {
      this.frequency.currentQuantity = code;
    } else {
      this.strength.currentOTPQuantity = code;
    }

    this.quantity = this.findQuantity();
    this.publishStateChange();
  }

  public publishStateChange(): void {
    Events.fire("product-state-change", {
      product: this.product,
      strength: this.strength,
      frequency: this.frequency,
      quantity: this.quantity,
    });
  }

  public getCurrentState(): ProductState {
    return {
      product: this.product,
      strength: this.strength,
      frequency: this.frequency,
      quantity: this.quantity,
    };
  }

  public getProducts(): Schema.Products {
    return this.products;
  }

  public static getInstance(): ProductPageState {
    if (this.INSTANCE == null) {
      this.INSTANCE = new ProductPageState();
    }

    return this.INSTANCE;
  }

  public static getInstanceWithPrices(pricesOverride: ProductsForCategoryAPIResult): ProductPageState {
    this.INSTANCE = new ProductPageState(pricesOverride);
    return this.INSTANCE;
  }
}
